import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Icon from './Icon';
import { getThumbnailUrl } from '../services/api';

interface KindInfo {
  label: string;
  icon: string;
  color: string;
}

const KINDS: Record<string, KindInfo> = {
  pdf:    { label: 'PDF', icon: 'file',    color: '#E5484D' },
  doc:    { label: 'DOC', icon: 'file',    color: '#2D5CF6' },
  sheet:  { label: 'XLS', icon: 'file',    color: '#1F8A5B' },
  slides: { label: 'PPT', icon: 'file',    color: '#E08C00' },
  image:  { label: 'IMG', icon: 'image',   color: '#7C3AED' },
  video:  { label: 'VID', icon: 'video',   color: '#0E1726' },
  audio:  { label: 'AUD', icon: 'music',   color: '#9333EA' },
  text:   { label: 'TXT', icon: 'file',    color: '#64748B' },
  zip:    { label: 'ZIP', icon: 'layers',  color: '#B45309' },
  design: { label: 'FIG', icon: 'sparkle', color: '#D6409F' },
};

const EXT_TO_KIND: Record<string, string> = {
  pdf: 'pdf',
  doc: 'doc', docx: 'doc',
  xls: 'sheet', xlsx: 'sheet',
  ppt: 'slides', pptx: 'slides',
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image',
  mp4: 'video', mov: 'video',
  mp3: 'audio', wav: 'audio',
  txt: 'text', md: 'text',
  zip: 'zip', rar: 'zip',
  fig: 'design', sketch: 'design',
};

function extFromName(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function kindFor(mime: string, ext: string): string {
  if (EXT_TO_KIND[ext]) return EXT_TO_KIND[ext];
  if (mime === 'image') return 'image';
  if (mime === 'video') return 'video';
  if (mime === 'audio') return 'audio';
  return 'text';
}

interface TypeGlyphProps {
  name: string;
  mime?: string;
  size?: number;
}

export default function TypeGlyph({ name, mime = 'application', size = 80 }: TypeGlyphProps) {
  const ext = extFromName(name);
  const kind = kindFor(mime, ext);
  const info = KINDS[kind] ?? KINDS.text;

  return (
    <div style={{
      width: size, height: size,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative',
      background: `color-mix(in oklab, ${info.color} 14%, var(--card))`,
      border: `1px solid color-mix(in oklab, ${info.color} 26%, var(--line))`,
      borderRadius: size * 0.22,
      flexShrink: 0,
    }}>
      <Icon name={info.icon} size={size * 0.42} color={info.color} />
      <span style={{
        position: 'absolute',
        bottom: size * 0.09,
        fontSize: Math.max(7, size * 0.16),
        fontWeight: 700,
        color: info.color,
        fontFamily: 'var(--font-mono)',
        letterSpacing: '0.04em',
      }}>{info.label}</span>
    </div>
  );
}

interface ThumbnailProps {
  item: { id?: string; name: string; mime_category?: string; scan_status?: string; previewUrl?: string; has_thumbnail?: boolean };
  width?: number;
  height?: number;
  // Fill the parent edge-to-edge instead of rendering a fixed-size box. Use in
  // grid tiles so the artwork spans the whole card with no gutter/frame.
  fill?: boolean;
}

export function Thumbnail({ item, width = 192, height = 124, fill = false }: ThumbnailProps) {
  const mime = item.mime_category ?? 'application';
  const scanning = item.scan_status === 'scanning' || item.scan_status === 'pending';

  // Lazily resolve a signed URL for the derived thumbnail. Only fired when the
  // API told us one exists (has_thumbnail), so we never spray 404s across a grid.
  const { data: fetchedUrl } = useQuery({
    queryKey: ['thumbnail', item.id],
    queryFn: () => getThumbnailUrl(item.id as string),
    enabled: !!item.id && !!item.has_thumbnail && (mime === 'image' || mime === 'video') && !scanning,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const previewSrc = item.previewUrl ?? fetchedUrl;
  const box: React.CSSProperties = fill ? { width: '100%', height: '100%' } : { width, height };
  const glyphSize = (fill ? 124 : Math.min(width, height)) * 0.55;

  if (scanning) {
    return (
      <div style={{ ...box, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--paper-2)' }}>
        <span className="t-skeleton" style={{ width: 48, height: 48, borderRadius: 8 }} />
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Generating…</span>
      </div>
    );
  }

  if ((mime === 'image' || mime === 'video') && previewSrc) {
    return (
      <div style={{ ...box, position: 'relative', background: '#000' }}>
        <img src={previewSrc} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        {mime === 'video' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="video" size={16} color="white" />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper-2)' }}>
      <TypeGlyph name={item.name} mime={mime} size={glyphSize} />
    </div>
  );
}
